import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Text,
  Title,
  Metric,
  Table,
  TableHead,
  TableRow,
  TableHeaderCell,
  TableBody,
  TableCell,
  Grid,
} from '@tremor/react';
import {
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  ComposedChart,
  LabelList,
} from 'recharts';
import kpiAPI from '../../api/kpi';
import DatePicker from './DatePicker';
import './KPIStatPage.css';

const PROJECT = 'MAN';

const STORAGE_KEY_DATE_FROM = 'transport-dateFrom';
const STORAGE_KEY_DATE_TO = 'transport-dateTo';

function getInitialDateFrom() {
  try {
    const s = localStorage.getItem(STORAGE_KEY_DATE_FROM);
    if (s && /^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  } catch (e) {}
  const d = new Date();
  d.setDate(d.getDate() - 7);
  return formatDateForInput(d);
}

function getInitialDateTo() {
  try {
    const s = localStorage.getItem(STORAGE_KEY_DATE_TO);
    if (s && /^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  } catch (e) {}
  return formatDateForInput(new Date());
}

const METRIC_KEYS = [
  { key: 'SHIP SQ', label: 'SHIP SQ' },
  { key: 'SHIP REP', label: 'SHIP REP' },
  { key: 'SHIP LERGUT', label: 'SHIP LERGUT' },
];

const ZMIANA_OPTIONS = [
  { key: 'both', label: 'A i B' },
  { key: 'A', label: 'A' },
  { key: 'B', label: 'B' },
];

/** Kolejność przełączników na wykresie trendu (A vs B) + średnia 7D */
const TREND_METRIC_OPTIONS = [
  { key: 'SHIP SQ', label: 'SHIP SQ' },
  { key: 'SHIP REP', label: 'SHIP REP' },
  { key: 'SHIP LERGUT', label: 'SHIP LERGUT' },
];

const CHART_COLORS = { A: '#00bcd4', B: '#ff9800', maA: '#4caf50', maB: '#f44336' };

/** Elementy legendy wykresu trendu – klikalne przełączniki serii */
const TREND_LEGEND_ITEMS = [
  { key: 'A', label: 'A', color: CHART_COLORS.A },
  { key: 'B', label: 'B', color: CHART_COLORS.B },
  { key: 'maA', label: 'Śr. 7D (A)', color: CHART_COLORS.maA },
  { key: 'maB', label: 'Śr. 7D (B)', color: CHART_COLORS.maB },
];

function normalizeSheetData(data) {
  if (!data || !Array.isArray(data)) return [];
  return data.map((row) => {
    if (!Array.isArray(row)) return [{ value: '' }];
    return row.map((cell) => {
      if (cell && typeof cell === 'object' && 'value' in cell) return cell;
      return { value: cell != null ? String(cell) : '' };
    });
  });
}

function formatDateForInput(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function parseDateOnly(val) {
  if (val == null || val === '') return null;
  const s = String(val).trim();
  if (!s) return null;
  const datePart = s.includes(' ') ? s.split(/\s/)[0] : s;
  const iso = /^\d{4}-\d{2}-\d{2}/.exec(datePart);
  if (iso) return new Date(iso[0]);
  const ddmmyy = datePart.match(/^(\d{1,2})[./](\d{1,2})[./](\d{4})$/);
  if (ddmmyy) return new Date(parseInt(ddmmyy[3], 10), parseInt(ddmmyy[2], 10) - 1, parseInt(ddmmyy[1], 10));
  const d = new Date(datePart);
  return Number.isNaN(d.getTime()) ? null : d;
}

function getColumnIndex(headers, name) {
  const needle = String(name || '').trim().toLowerCase();
  const idx = headers.findIndex((h) => String(h || '').trim().toLowerCase() === needle);
  return idx >= 0 ? idx : -1;
}

function parseNumber(val) {
  if (val == null || val === '') return 0;
  const v = typeof val === 'object' && val !== null && 'value' in val ? val.value : val;
  const n = Number(String(v).replace(/\s/g, '').replace(',', '.'));
  return Number.isNaN(n) ? 0 : n;
}

function dateToStr(d) {
  return d ? formatDateForInput(d) : '';
}

function getStartOfWeek(d) {
  const x = new Date(d);
  const day = x.getDay();
  const diff = day === 0 ? 6 : day - 1;
  x.setDate(x.getDate() - diff);
  x.setHours(0, 0, 0, 0);
  return x;
}

function getStartOfMonth(d) {
  const x = new Date(d.getFullYear(), d.getMonth(), 1);
  x.setHours(0, 0, 0, 0);
  return x;
}

/** Filtruje wiersze po zakresie dat (DATA w arkuszu). rangeFrom/To to stringi YYYY-MM-DD. */
function filterRowsByDateRange(rows, headers, rangeFrom, rangeTo) {
  if (!rows.length || !rangeFrom || !rangeTo) return rows;
  const col = getColumnIndex(headers, 'DATA');
  if (col < 0) return rows;
  const from = new Date(rangeFrom);
  const to = new Date(rangeTo);
  to.setHours(23, 59, 59, 999);
  return rows.filter((row) => {
    const val = row[col] && typeof row[col] === 'object' ? row[col].value : row[col];
    const d = parseDateOnly(val);
    if (!d) return false;
    const t = d.getTime();
    return t >= from.getTime() && t <= to.getTime();
  });
}

/** Filtruje po ZMIANA: A, B lub obie. */
function filterRowsByZmiana(rows, headers, zmianaFilter) {
  if (zmianaFilter === 'both' || !rows.length) return rows;
  const col = getColumnIndex(headers, 'ZMIANA');
  if (col < 0) return rows;
  return rows.filter((row) => {
    const val = row[col] && typeof row[col] === 'object' ? row[col].value : row[col];
    const z = String(val ?? '').trim().toUpperCase();
    return z === zmianaFilter;
  });
}

/** Zwraca mapę: dataStr -> { A: sum, B: sum } dla danej metryki. */
function aggregateByDayAndZmiana(rows, headers, metricKey) {
  const dataCol = getColumnIndex(headers, 'DATA');
  const zmianaCol = getColumnIndex(headers, 'ZMIANA');
  const metricCol = getColumnIndex(headers, metricKey);
  if (dataCol < 0 || metricCol < 0) return {};
  const byDay = {};
  rows.forEach((row) => {
    const val = row[dataCol] && typeof row[dataCol] === 'object' ? row[dataCol].value : row[dataCol];
    const d = parseDateOnly(val);
    if (!d) return;
    const dateStr = dateToStr(d);
    const z = zmianaCol >= 0
      ? String((row[zmianaCol] && typeof row[zmianaCol] === 'object' ? row[zmianaCol].value : row[zmianaCol]) ?? '').trim().toUpperCase()
      : '';
    const zm = z === 'A' || z === 'B' ? z : 'A';
    if (!byDay[dateStr]) byDay[dateStr] = { A: 0, B: 0 };
    byDay[dateStr][zm] += parseNumber(row[metricCol]);
  });
  return byDay;
}

/** KPI dla jednej metryki: Ostatni dzień (w danych), Poprzedni+DoD%, Średnia 7D, MTD, WTD. */
function computeKpiForMetric(rows, headers, metricKey, rangeFrom, rangeTo) {
  const byDay = aggregateByDayAndZmiana(rows, headers, metricKey);
  const daySum = (dateStr) => {
    const o = byDay[dateStr] || { A: 0, B: 0 };
    return o.A + o.B;
  };
  const sortedDates = Object.keys(byDay).sort();
  const lastDayStr = sortedDates.length ? sortedDates[sortedDates.length - 1] : null;
  const prevDayStr = sortedDates.length > 1 ? sortedDates[sortedDates.length - 2] : null;
  const lastDaySum = lastDayStr ? daySum(lastDayStr) : 0;
  const prevDaySum = prevDayStr ? daySum(prevDayStr) : 0;
  const dodPct = prevDaySum !== 0 ? (((lastDaySum - prevDaySum) / prevDaySum) * 100).toFixed(1) : (lastDaySum > 0 ? '100' : '0');
  const changeAmount = prevDayStr != null ? lastDaySum - prevDaySum : null;
  const last7 = sortedDates.slice(-7);
  const avg7d = last7.length ? (last7.reduce((s, d) => s + daySum(d), 0) / last7.length).toFixed(1) : '—';

  const rangeEnd = rangeTo ? new Date(rangeTo) : new Date();
  const rangeStart = rangeFrom ? new Date(rangeFrom) : new Date(rangeEnd.getFullYear(), rangeEnd.getMonth(), 1);
  const mtdStart = getStartOfMonth(rangeEnd);
  const mtdFrom = rangeStart.getTime() > mtdStart.getTime() ? rangeStart : mtdStart;
  const mtdTo = rangeEnd;
  let mtdSum = 0;
  for (let d = new Date(mtdFrom); d <= mtdTo; d.setDate(d.getDate() + 1)) {
    mtdSum += daySum(dateToStr(d));
  }

  const wtdStart = getStartOfWeek(rangeEnd);
  const wtdFrom = rangeStart.getTime() > wtdStart.getTime() ? rangeStart : wtdStart;
  const wtdTo = rangeEnd;
  let wtdSum = 0;
  for (let d = new Date(wtdFrom); d <= wtdTo; d.setDate(d.getDate() + 1)) {
    wtdSum += daySum(dateToStr(d));
  }

  return {
    lastDay: lastDaySum,
    lastDayStr,
    prevDay: prevDaySum,
    dodPct,
    changeAmount,
    avg7d,
    mtd: mtdSum,
    wtd: wtdSum,
  };
}

/** Dane do wykresu liniowego: DATA, A, B, MA7_A, MA7_B. */
function buildLineChartData(rows, headers, metricKey) {
  const byDay = aggregateByDayAndZmiana(rows, headers, metricKey);
  const sortedDates = Object.keys(byDay).sort();
  const data = sortedDates.map((dateStr) => {
    const o = byDay[dateStr] || { A: 0, B: 0 };
    return { dateStr, A: o.A, B: o.B, total: o.A + o.B };
  });
  for (let i = 0; i < data.length; i++) {
    const from = Math.max(0, i - 6);
    const slice = data.slice(from, i + 1);
    data[i].maA = slice.length ? (slice.reduce((s, x) => s + x.A, 0) / slice.length).toFixed(2) : null;
    data[i].maB = slice.length ? (slice.reduce((s, x) => s + x.B, 0) / slice.length).toFixed(2) : null;
  }
  return data;
}

/** Dane do słupków grupowanych A vs B per dzień (jedna metryka). */
function buildGroupedBarData(rows, headers, metricKey) {
  const byDay = aggregateByDayAndZmiana(rows, headers, metricKey);
  return Object.keys(byDay)
    .sort()
    .map((dateStr) => ({
      dateStr,
      A: (byDay[dateStr] || {}).A || 0,
      B: (byDay[dateStr] || {}).B || 0,
    }));
}

/** Dane do słupków skumulowanych: DATA, SHIP SQ, SHIP REP, SHIP LERGUT (A+B łącznie). */
function buildStackedBarData(rows, headers) {
  const dataCol = getColumnIndex(headers, 'DATA');
  const sqCol = getColumnIndex(headers, 'SHIP SQ');
  const repCol = getColumnIndex(headers, 'SHIP REP');
  const lergutCol = getColumnIndex(headers, 'SHIP LERGUT');
  if (dataCol < 0 || sqCol < 0 || repCol < 0 || lergutCol < 0) return [];
  const byDay = {};
  rows.forEach((row) => {
    const val = row[dataCol] && typeof row[dataCol] === 'object' ? row[dataCol].value : row[dataCol];
    const d = parseDateOnly(val);
    if (!d) return;
    const dateStr = dateToStr(d);
    if (!byDay[dateStr]) byDay[dateStr] = { 'SHIP SQ': 0, 'SHIP REP': 0, 'SHIP LERGUT': 0 };
    byDay[dateStr]['SHIP SQ'] += parseNumber(row[sqCol]);
    byDay[dateStr]['SHIP REP'] += parseNumber(row[repCol]);
    byDay[dateStr]['SHIP LERGUT'] += parseNumber(row[lergutCol]);
  });
  return Object.keys(byDay)
    .sort()
    .map((dateStr) => {
      const o = byDay[dateStr];
      const total = (o['SHIP SQ'] || 0) + (o['SHIP REP'] || 0) + (o['SHIP LERGUT'] || 0);
      return { dateStr, ...o, total, zero: 0 };
    });
}

/** Interaktywna legenda: klik w element włącza/wyłącza serię (nie można wyłączyć ostatniej). */
function TrendChartLegend({ visible, onToggle }) {
  return (
    <div className="recharts-default-legend" style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 16, marginTop: 8 }}>
      {TREND_LEGEND_ITEMS.map(({ key, label, color }) => (
        <span
          key={key}
          role="button"
          tabIndex={0}
          onClick={() => onToggle(key)}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggle(key); } }}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            cursor: 'pointer',
            opacity: visible[key] ? 1 : 0.45,
            userSelect: 'none',
          }}
        >
          <span style={{ width: 14, height: 14, borderRadius: 2, background: color }} />
          <span style={{ color: '#94a3b8', fontSize: 12 }}>{label}</span>
        </span>
      ))}
    </div>
  );
}

/** Tabela: wiersze = unikalne daty (posortowane), kolumny = SHIP SQ (A), SHIP SQ (B), ... */
function buildTableData(rows, headers) {
  const byDay = {};
  const dataCol = getColumnIndex(headers, 'DATA');
  const zmianaCol = getColumnIndex(headers, 'ZMIANA');
  const keys = ['SHIP SQ', 'SHIP REP', 'SHIP LERGUT'];
  const cols = { DATA: dataCol, ZMIANA: zmianaCol };
  keys.forEach((k) => { cols[k] = getColumnIndex(headers, k); });
  if (dataCol < 0) return [];
  rows.forEach((row) => {
    const val = row[dataCol] && typeof row[dataCol] === 'object' ? row[dataCol].value : row[dataCol];
    const d = parseDateOnly(val);
    if (!d) return;
    const dateStr = dateToStr(d);
    const z = zmianaCol >= 0
      ? String((row[zmianaCol] && typeof row[zmianaCol] === 'object' ? row[zmianaCol].value : row[zmianaCol]) ?? '').trim().toUpperCase()
      : '';
    const zm = z === 'A' || z === 'B' ? z : 'A';
    if (!byDay[dateStr]) byDay[dateStr] = { dateStr, 'SHIP SQ_A': 0, 'SHIP SQ_B': 0, 'SHIP REP_A': 0, 'SHIP REP_B': 0, 'SHIP LERGUT_A': 0, 'SHIP LERGUT_B': 0 };
    keys.forEach((k) => {
      const c = cols[k];
      if (c >= 0) byDay[dateStr][`${k}_${zm}`] += parseNumber(row[c]);
    });
  });
  return Object.values(byDay).sort((a, b) => a.dateStr.localeCompare(b.dateStr));
}

function Transport() {
  const [raportSheet, setRaportSheet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dateFrom, setDateFrom] = useState(getInitialDateFrom);
  const [dateTo, setDateTo] = useState(getInitialDateTo);
  const [zmianaFilter, setZmianaFilter] = useState('both');
  const [trendMetric, setTrendMetric] = useState('SHIP SQ');
  const [groupedBarMetric, setGroupedBarMetric] = useState('SHIP SQ');
  const [trendSeriesVisible, setTrendSeriesVisible] = useState({ A: true, B: true, maA: true, maB: true });

  const handleTrendLegendClick = useCallback((key) => {
    setTrendSeriesVisible((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      const visibleCount = Object.values(next).filter(Boolean).length;
      if (visibleCount === 0) return prev;
      return next;
    });
  }, []);

  const loadReports = useCallback(async () => {
    setError(null);
    setLoading(true);
    try {
      const res = await kpiAPI.getReports(PROJECT);
      const list = Array.isArray(res?.sheets) ? res.sheets : [];
      const raport = list.find((s) => String(s.name || '').trim() === 'Raport');
      setRaportSheet(raport ? { name: raport.name, data: normalizeSheetData(raport.data) } : null);
      if (!raport) setError('Brak arkusza „Raport” w Uzupełnij raport.');
    } catch (err) {
      console.error('Błąd ładowania raportu Transport:', err);
      setError('Nie udało się załadować danych z arkusza Raport.');
      setRaportSheet(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadReports();
  }, [loadReports]);

  useEffect(() => {
    try {
      if (dateFrom) localStorage.setItem(STORAGE_KEY_DATE_FROM, dateFrom);
      if (dateTo) localStorage.setItem(STORAGE_KEY_DATE_TO, dateTo);
    } catch (e) {}
  }, [dateFrom, dateTo]);

  const headers = raportSheet?.data?.[0]?.map((c) => (c && typeof c === 'object' ? c.value : c)) ?? [];
  const dataRows = raportSheet?.data?.slice(1) ?? [];
  const filteredByDate = filterRowsByDateRange(dataRows, headers, dateFrom, dateTo);
  const filteredRows = filterRowsByZmiana(filteredByDate, headers, zmianaFilter);

  const kpiSq = raportSheet ? computeKpiForMetric(filteredRows, headers, 'SHIP SQ', dateFrom, dateTo) : null;
  const kpiRep = raportSheet ? computeKpiForMetric(filteredRows, headers, 'SHIP REP', dateFrom, dateTo) : null;
  const kpiLergut = raportSheet ? computeKpiForMetric(filteredRows, headers, 'SHIP LERGUT', dateFrom, dateTo) : null;

  const lineDataByMetric = {
    'SHIP SQ': buildLineChartData(filteredRows, headers, 'SHIP SQ'),
    'SHIP REP': buildLineChartData(filteredRows, headers, 'SHIP REP'),
    'SHIP LERGUT': buildLineChartData(filteredRows, headers, 'SHIP LERGUT'),
  };
  const lineDataTrend = lineDataByMetric[trendMetric] || [];
  const groupedBarByMetric = {
    'SHIP SQ': buildGroupedBarData(filteredRows, headers, 'SHIP SQ'),
    'SHIP REP': buildGroupedBarData(filteredRows, headers, 'SHIP REP'),
    'SHIP LERGUT': buildGroupedBarData(filteredRows, headers, 'SHIP LERGUT'),
  };
  const groupedBarData = groupedBarByMetric[groupedBarMetric] || [];
  const stackedData = buildStackedBarData(filteredRows, headers);
  const tableData = buildTableData(filteredRows, headers);

  const tableColumns = [
    { key: 'dateStr', label: 'DATA' },
    { key: 'SHIP SQ_A', label: 'SHIP SQ (A)' },
    { key: 'SHIP SQ_B', label: 'SHIP SQ (B)' },
    { key: 'SHIP REP_A', label: 'SHIP REP (A)' },
    { key: 'SHIP REP_B', label: 'SHIP REP (B)' },
    { key: 'SHIP LERGUT_A', label: 'SHIP LERGUT (A)' },
    { key: 'SHIP LERGUT_B', label: 'SHIP LERGUT (B)' },
  ];

  return (
    <div className="kpi-stat-page">
      <div className="kpi-ps-header-bar">
        <Title className="kpi-ps-header-title">Transport</Title>
        <div className="kpi-ps-zakres">
          <span className="kpi-ps-zakres-title">Zakres dat</span>
          <div className="kpi-ps-zakres-inner">
            <DatePicker label="Od:" value={dateFrom} onChange={(v) => setDateFrom(v || '')} max={dateTo || undefined} placeholder="Od" inline align="left" className="kpi-ps-zakres-date" />
            <DatePicker label="Do:" value={dateTo} onChange={(v) => setDateTo(v || '')} min={dateFrom || undefined} placeholder="Do" inline align="right" className="kpi-ps-zakres-date" />
          </div>
          <span className="kpi-ps-zakres-title" style={{ marginLeft: 8 }}>ZMIANA</span>
          <div className="kpi-ps-zakres-buttons" role="group">
            {ZMIANA_OPTIONS.map((o) => (
              <button
                key={o.key}
                type="button"
                className={`kpi-ps-zakres-btn ${zmianaFilter === o.key ? 'kpi-ps-zakres-btn-active' : ''}`}
                onClick={() => setZmianaFilter(o.key)}
              >
                {o.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {error && <Text className="kpi-ps-table-error">{error}</Text>}
      {loading ? (
        <Text className="kpi-ps-table-loading">Ładowanie…</Text>
      ) : !raportSheet ? (
        <Text className="kpi-ps-table-empty">Brak arkusza „Raport” w Uzupełnij raport. Dodaj arkusz o nazwie Raport i wypełnij dane (DATA, ZMIANA, SHIP SQ, SHIP REP, SHIP LERGUT).</Text>
      ) : (
        <>
          {/* 3 rzędy kafelków KPI */}
          {METRIC_KEYS.map(({ key: metricKey, label }) => {
            const kpi = metricKey === 'SHIP SQ' ? kpiSq : metricKey === 'SHIP REP' ? kpiRep : kpiLergut;
            return (
              <Card key={metricKey} className="kpi-ps-shell" style={{ marginBottom: 16 }}>
                <Text className="kpi-ps-kpi-label" style={{ marginBottom: 8 }}>{label}</Text>
                <Grid numItemsSm={2} numItemsMd={3} numItemsLg={6} className="kpi-ps-cards">
                  <Card className="kpi-ps-kpi-card">
                    <Text className="kpi-ps-kpi-label">Ostatni dzień{kpi?.lastDayStr ? ` (${kpi.lastDayStr})` : ''}</Text>
                    <Metric className="kpi-ps-kpi-metric">{kpi?.lastDay ?? '—'}</Metric>
                  </Card>
                  <Card className="kpi-ps-kpi-card">
                    <Text className="kpi-ps-kpi-label">% zmiana (ostatni vs poprzedni)</Text>
                    <Metric className="kpi-ps-kpi-metric">{kpi?.dodPct ?? '—'}%{kpi?.changeAmount != null ? ` (${kpi.changeAmount >= 0 ? '+' : ''}${kpi.changeAmount})` : ''}</Metric>
                  </Card>
                  <Card className="kpi-ps-kpi-card">
                    <Text className="kpi-ps-kpi-label">Średnia 7 dni</Text>
                    <Metric className="kpi-ps-kpi-metric">{kpi?.avg7d ?? '—'}</Metric>
                  </Card>
                  <Card className="kpi-ps-kpi-card">
                    <Text className="kpi-ps-kpi-label">Od początku miesiąca do dziś.</Text>
                    <Metric className="kpi-ps-kpi-metric">{kpi?.mtd ?? '—'}</Metric>
                  </Card>
                  <Card className="kpi-ps-kpi-card">
                    <Text className="kpi-ps-kpi-label">Od początku tygodnia do dziś.</Text>
                    <Metric className="kpi-ps-kpi-metric">{kpi?.wtd ?? '—'}</Metric>
                  </Card>
                </Grid>
              </Card>
            );
          })}

          {/* Jeden wykres trendu (A vs B) + średnia 7D – metryka wybierana przełącznikami */}
          <Card className="kpi-ps-charts-area">
            <Text className="kpi-ps-chart-block-title" style={{ marginBottom: 8 }}>Trend (A vs B) + średnia krocząca 7D</Text>
            <div className="kpi-ps-zakres-buttons" role="group" style={{ marginBottom: 12 }}>
              {TREND_METRIC_OPTIONS.map((o) => (
                <button
                  key={o.key}
                  type="button"
                  className={`kpi-ps-zakres-btn ${trendMetric === o.key ? 'kpi-ps-zakres-btn-active' : ''}`}
                  onClick={() => setTrendMetric(o.key)}
                >
                  {o.label}
                </button>
              ))}
            </div>
            {lineDataTrend.length === 0 ? (
              <div className="kpi-ps-chart-empty">Brak danych w wybranym zakresie.</div>
            ) : (
              <ResponsiveContainer width="100%" height={320}>
                <ComposedChart data={lineDataTrend} margin={{ top: 8, right: 24, left: 8, bottom: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" strokeOpacity={0.6} />
                  <XAxis dataKey="dateStr" angle={-45} textAnchor="end" height={48} tick={{ fill: '#94a3b8', fontSize: 11 }} />
                  <YAxis allowDecimals={false} tick={{ fill: '#94a3b8', fontSize: 12 }} />
                  <Tooltip cursor={false} contentStyle={{ background: '#fff', color: '#1e293b', borderRadius: 8 }} />
                  <Legend content={<TrendChartLegend visible={trendSeriesVisible} onToggle={handleTrendLegendClick} />} />
                  <Line type="monotone" dataKey="A" stroke={CHART_COLORS.A} strokeWidth={trendSeriesVisible.A ? 2 : 0} strokeOpacity={trendSeriesVisible.A ? 1 : 0} name="A" dot={false} hide={!trendSeriesVisible.A} />
                  <Line type="monotone" dataKey="B" stroke={CHART_COLORS.B} strokeWidth={trendSeriesVisible.B ? 2 : 0} strokeOpacity={trendSeriesVisible.B ? 1 : 0} name="B" dot={false} hide={!trendSeriesVisible.B} />
                  <Line type="monotone" dataKey="maA" stroke={CHART_COLORS.maA} strokeWidth={trendSeriesVisible.maA ? 1.5 : 0} strokeOpacity={trendSeriesVisible.maA ? 1 : 0} strokeDasharray="4 4" name="Śr. 7D (A)" dot={false} hide={!trendSeriesVisible.maA} />
                  <Line type="monotone" dataKey="maB" stroke={CHART_COLORS.maB} strokeWidth={trendSeriesVisible.maB ? 1.5 : 0} strokeOpacity={trendSeriesVisible.maB ? 1 : 0} strokeDasharray="4 4" name="Śr. 7D (B)" dot={false} hide={!trendSeriesVisible.maB} />
                </ComposedChart>
              </ResponsiveContainer>
            )}
          </Card>

          {/* Porównanie A vs B per dzień – metryka wybierana przełącznikiem */}
          <Card className="kpi-ps-charts-area">
            <Text className="kpi-ps-chart-block-title" style={{ marginBottom: 8 }}>Porównanie A vs B per dzień</Text>
            <div className="kpi-ps-zakres-buttons" role="group" style={{ marginBottom: 12 }}>
              {METRIC_KEYS.map(({ key }) => (
                <button
                  key={key}
                  type="button"
                  className={`kpi-ps-zakres-btn ${groupedBarMetric === key ? 'kpi-ps-zakres-btn-active' : ''}`}
                  onClick={() => setGroupedBarMetric(key)}
                >
                  {key}
                </button>
              ))}
            </div>
            {groupedBarData.length === 0 ? (
              <div className="kpi-ps-chart-empty">Brak danych w wybranym zakresie.</div>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={groupedBarData} margin={{ top: 28, right: 24, left: 8, bottom: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" strokeOpacity={0.6} />
                  <XAxis dataKey="dateStr" angle={-45} textAnchor="end" height={48} tick={{ fill: '#94a3b8', fontSize: 11 }} />
                  <YAxis allowDecimals={false} tick={{ fill: '#94a3b8', fontSize: 12 }} />
                  <Tooltip cursor={false} contentStyle={{ background: '#fff', color: '#1e293b', borderRadius: 8 }} />
                  <Legend />
                  <Bar dataKey="A" fill={CHART_COLORS.A} name="A" barSize={24} radius={[4, 4, 0, 0]}>
                    <LabelList dataKey="A" position="top" fill="#f1f5f9" fontSize={13} style={{ fontWeight: 600 }} formatter={(v) => (v != null && v !== '' ? String(Math.round(Number(v))) : '')} />
                  </Bar>
                  <Bar dataKey="B" fill={CHART_COLORS.B} name="B" barSize={24} radius={[4, 4, 0, 0]}>
                    <LabelList dataKey="B" position="top" fill="#f1f5f9" fontSize={13} style={{ fontWeight: 600 }} formatter={(v) => (v != null && v !== '' ? String(Math.round(Number(v))) : '')} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </Card>

          {/* Słupki skumulowane SQ + REP + LERGUT */}
          <Card className="kpi-ps-charts-area">
            <Text className="kpi-ps-chart-block-title">Transport - Suma</Text>
            {stackedData.length === 0 ? (
              <div className="kpi-ps-chart-empty">Brak danych.</div>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={stackedData} margin={{ top: 28, right: 24, left: 8, bottom: 8 }} stackOffset="sign">
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" strokeOpacity={0.6} />
                  <XAxis dataKey="dateStr" angle={-45} textAnchor="end" height={48} tick={{ fill: '#94a3b8', fontSize: 11 }} />
                  <YAxis allowDecimals={false} tick={{ fill: '#94a3b8', fontSize: 12 }} />
                  <Tooltip cursor={false} contentStyle={{ background: '#fff', color: '#1e293b', borderRadius: 8 }} />
                  <Legend />
                  <Bar dataKey="SHIP SQ" stackId="a" fill="#00bcd4" name="SHIP SQ" barSize={24} radius={[0, 0, 0, 0]} />
                  <Bar dataKey="SHIP REP" stackId="a" fill="#ff9800" name="SHIP REP" barSize={24} radius={[0, 0, 0, 0]} />
                  <Bar dataKey="SHIP LERGUT" stackId="a" fill="#4caf50" name="SHIP LERGUT" barSize={24} radius={[4, 4, 0, 0]} />
                  <Bar dataKey="zero" stackId="a" fill="transparent" barSize={24} isAnimationActive={false} legendType="none">
                    <LabelList dataKey="total" position="top" fill="#f1f5f9" fontSize={13} style={{ fontWeight: 600 }} formatter={(v) => (v > 0 ? String(Math.round(Number(v))) : '')} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </Card>

          {/* Tabela */}
          <Card className="kpi-ps-table-card">
            <Title className="kpi-ps-table-title">Szczegółowe podsumowanie</Title>
            <Table className="kpi-ps-table">
              <TableHead>
                <TableRow>
                  {tableColumns.map((col) => (
                    <TableHeaderCell key={col.key}>{col.label}</TableHeaderCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {tableData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={tableColumns.length} className="kpi-ps-table-empty-cell">
                      Brak danych w wybranym zakresie.
                    </TableCell>
                  </TableRow>
                ) : (
                  tableData.map((row, idx) => (
                    <TableRow key={idx}>
                      {tableColumns.map((col) => (
                        <TableCell key={col.key}>{row[col.key] ?? '—'}</TableCell>
                      ))}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Card>
        </>
      )}
    </div>
  );
}

export default Transport;
